import { GeometryBounds2D } from "../components/openingGeometry";
import {
  defaultDoorHeight,
  defaultDoorWidth,
} from "shared/utils/doorSceneConstants";

const deMaasConfig = {
  centerY: 1.285,
  centerXBase: -0.075,
  reduceScaleWidth: 1050,
  reduceScaleHeight: 2025,
  reduceScaleFactor: 1.1,
  reduceScaleXAdjustment: 0.04,
};

export interface DeMaasLayoutMetrics {
  scale: number;
  scaleDoorX: number;
  scaleDoorY: number;
  centerX: number;
  centerY: number;
}

export interface VerticalWindow {
  topY: number;
  bottomY: number;
}

export interface VerticalWindowBeam {
  height: number;
  handleAnchor: boolean;
}

export const maxDeMaasVisibleCurveHeight = 1.7;

export const getDeMaasLayoutMetrics = (
  width: number,
  height: number,
): DeMaasLayoutMetrics => {
  const scaleDoorY = height / defaultDoorHeight;
  const scaleDoorX = width / defaultDoorWidth;

  const shouldReduceScale =
    width >= deMaasConfig.reduceScaleWidth &&
    height < deMaasConfig.reduceScaleHeight;

  const scale = shouldReduceScale
    ? scaleDoorX / deMaasConfig.reduceScaleFactor
    : scaleDoorX;

  const centerY = deMaasConfig.centerY * scaleDoorY;

  const xAdjustment = shouldReduceScale
    ? deMaasConfig.reduceScaleXAdjustment
    : 0;
  const centerX = deMaasConfig.centerXBase * scaleDoorX - xAdjustment;

  return {
    scale,
    scaleDoorX,
    scaleDoorY,
    centerX,
    centerY,
  };
};

export const getBeamInnerVerticalWindow = (
  topBeamCenterY: number,
  bottomBeamCenterY: number,
  beamBounds: GeometryBounds2D,
): VerticalWindow => {
  return {
    topY: topBeamCenterY + beamBounds.minY,
    bottomY: bottomBeamCenterY + beamBounds.maxY,
  };
};

export const getDeMaasVerticalWindow = ({
  layoutCode,
  currentHeight,
  beamBounds,
  beams,
  isLeafStyleDeMaasLayout,
  beamInsetMeters,
}: {
  layoutCode: string | null | undefined;
  currentHeight: number;
  beamBounds: GeometryBounds2D;
  beams: VerticalWindowBeam[] | null | undefined;
  isLeafStyleDeMaasLayout: boolean;
  beamInsetMeters: number;
}): VerticalWindow | null => {
  if (layoutCode === "demaas" || layoutCode === "demaasdubbel") {
    return getBeamInnerVerticalWindow(
      currentHeight / 1000 - beamInsetMeters,
      beamInsetMeters,
      beamBounds,
    );
  }

  if (!isLeafStyleDeMaasLayout) {
    return null;
  }

  let topBeamCenterY = Number.NEGATIVE_INFINITY;
  let bottomBeamCenterY = Number.POSITIVE_INFINITY;
  let anchorBeamCount = 0;

  for (const beam of beams ?? []) {
    if (!beam.handleAnchor) {
      continue;
    }

    const beamCenterY = beam.height / 1000;
    topBeamCenterY = Math.max(topBeamCenterY, beamCenterY);
    bottomBeamCenterY = Math.min(bottomBeamCenterY, beamCenterY);
    anchorBeamCount++;
  }

  if (anchorBeamCount < 2) {
    return null;
  }

  return getBeamInnerVerticalWindow(
    topBeamCenterY,
    bottomBeamCenterY,
    beamBounds,
  );
};

export const getConstrainedDeMaasLayoutMetrics = ({
  preferredMetrics,
  curveBounds,
  openingLeftX,
  verticalWindow,
}: {
  preferredMetrics: DeMaasLayoutMetrics;
  curveBounds: GeometryBounds2D;
  openingLeftX: number;
  verticalWindow: VerticalWindow;
}): DeMaasLayoutMetrics => {
  const curveHeight = curveBounds.maxY - curveBounds.minY;
  const windowHeight = verticalWindow.topY - verticalWindow.bottomY;

  if (curveHeight <= 0 || windowHeight <= 0) {
    return preferredMetrics;
  }

  const allowedCurveHeight = Math.min(
    windowHeight,
    maxDeMaasVisibleCurveHeight,
  );
  const maxAllowedScale = allowedCurveHeight / curveHeight;
  const scale = Math.min(preferredMetrics.scale, maxAllowedScale);

  const scaledCurveCenterY =
    ((curveBounds.minY + curveBounds.maxY) / 2) * scale;
  const windowCenterY = (verticalWindow.topY + verticalWindow.bottomY) / 2;
  const centerY = windowCenterY - scaledCurveCenterY;
  const centerX = openingLeftX - curveBounds.minX * scale;

  return {
    ...preferredMetrics,
    scale,
    centerX,
    centerY,
  };
};

export const getDeMaasBeamY = (
  beamYAtDefault: number,
  metrics: DeMaasLayoutMetrics,
): number => {
  const offsetFromCenter = beamYAtDefault - deMaasConfig.centerY;
  return metrics.centerY + offsetFromCenter * metrics.scale;
};
